import useMailMergeFunctions from 'components/Dashboard/Influence/CrmEmails/MailMergeSection/useMailMergeFunctions';
import dynamicTextImage from 'components/Dashboard/Influence/CrmEmails/TinyEditor/dynamicTextImage';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { useEmailState } from 'components/EmailStateProvider';
import { useRef, useEffect, useState } from 'react';

// Helper function to find the @ sign closest to a given position (range or bookmark)
const findClosestAtSign = (editor, referenceRange, referenceBookmark = null, skipInitial = false) => {
  if (skipInitial) {
    return null;
  }
  try {
    let referenceNode = null;
    let referenceOffset = 0;

    // Get reference position from bookmark if provided, otherwise use range
    if (referenceBookmark) {
      try {
        // Save current position
        const currentRange = editor.selection.getRng();
        const currentBookmark = editor.selection.bookmarkManager.getBookmark(2, true);

        // Move to bookmark to get its position
        editor.selection.bookmarkManager.moveToBookmark(referenceBookmark);
        const bookmarkRange = editor.selection.getRng();
        referenceNode = bookmarkRange.startContainer;
        referenceOffset = bookmarkRange.startOffset;

        // Restore original position
        if (currentBookmark) {
          try {
            editor.selection.bookmarkManager.moveToBookmark(currentBookmark);
          } catch (e) {
            editor.selection.setRng(currentRange);
          }
        } else {
          editor.selection.setRng(currentRange);
        }
      } catch (e) {
        // If bookmark restore fails, use range
        referenceNode = referenceRange.startContainer;
        referenceOffset = referenceRange.startOffset;
      }
    } else {
      referenceNode = referenceRange.startContainer;
      referenceOffset = referenceRange.startOffset;
    }

    // Get all text nodes in the document
    const textNodes = [];
    const collectTextNodes = (node) => {
      if (node.nodeType === 3) {
        textNodes.push(node);
      } else {
        if (!node.classList || !node.classList.contains('crm-mail-merge-substitution')) {
          for (let child of node.childNodes) {
            collectTextNodes(child);
          }
        }
      }
    };
    collectTextNodes(editor.getBody());

    // Find all @ signs and calculate their distance from reference position
    const atSigns = [];
    for (const textNode of textNodes) {
      const text = textNode.textContent;
      let searchIndex = 0;
      while (true) {
        const atIndex = text.indexOf('@', searchIndex);
        if (atIndex === -1) break;

        // Calculate distance from reference position to this @ sign
        let distance = Infinity;
        try {
          // If @ is in the same text node as reference
          if (textNode === referenceNode) {
            distance = Math.abs(atIndex - referenceOffset);
          } else {
            // Different nodes - use range comparison to calculate distance
            const atRange = document.createRange();
            atRange.setStart(textNode, atIndex);
            atRange.collapse(true);

            const refRange = document.createRange();
            refRange.setStart(referenceNode, referenceOffset);
            refRange.collapse(true);

            // Calculate distance by counting characters between them
            const tempRange = document.createRange();
            if (atRange.compareBoundaryPoints(Range.START_TO_START, refRange) < 0) {
              tempRange.setStart(textNode, atIndex);
              tempRange.setEnd(referenceNode, referenceOffset);
            } else {
              tempRange.setStart(referenceNode, referenceOffset);
              tempRange.setEnd(textNode, atIndex);
            }
            // Count characters between them
            distance = tempRange.toString().length;
          }
        } catch (e) {
          // Fallback: use simple offset difference if same node
          if (textNode === referenceNode) {
            distance = Math.abs(atIndex - referenceOffset);
          }
        }

        atSigns.push({
          textNode,
          atIndex,
          distance,
        });

        searchIndex = atIndex + 1;
      }
    }

    // Return the @ sign closest to reference position
    if (atSigns.length === 0) return null;

    // Sort by distance and return the closest one
    atSigns.sort((a, b) => a.distance - b.distance);
    return atSigns[0];
  } catch (err) {
    console.error('Error finding closest @ sign:', err);
    return null;
  }
};

const DynamicTextMenu = (props) => {
  const {
    setOpenDynamicTextMenu,
    editor,
    mailMergeFields = [],
    atSignBookmarkRef,
    bookmark,
    isSubjectMode = false,
    onSelectForSubject = null,
    filterTextProp = '',
  } = props;

  const { emailState, setEmailState } = useEmailState();
  const { currentEmailDetails, emailSelectedContacts } = emailState;
  const { hasMailMergeData } = currentEmailDetails;
  const selectedContacts = emailSelectedContacts.length || 0;

  const [filterText, setFilterText] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSavingEmailOnMailMerge, setIsSavingEmailOnMailMerge] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isPositionReady, setIsPositionReady] = useState(false);

  const isInitialMount = useRef(true);
  const { autoSaveEmailOnMailMerge } = useMailMergeFunctions();

  // Sync filterTextProp when in subject mode
  useEffect(() => {
    if (isSubjectMode && filterTextProp !== undefined) {
      setFilterText(filterTextProp);
    }
  }, [filterTextProp, isSubjectMode]);

  // Update menu position based on @ sign location in input
  useEffect(() => {
    if (!isSubjectMode) {
      setIsPositionReady(true);
      return;
    }

    const updatePosition = () => {
      const inputElement = document.querySelector('.mail-merge-subject');
      if (!inputElement) return;

      const rect = inputElement.getBoundingClientRect();
      const cursorPos = inputElement.selectionStart;
      const atPos = cursorPos - filterText.length - 1; // -1 for the @ character

      if (atPos < 0) {
        setMenuPosition({ top: rect.bottom, left: rect.left });
        setIsPositionReady(true);
        return;
      }

      const text = inputElement.value;
      const textBeforeAt = text.substring(0, atPos);

      // Calculate width of text before @ using canvas for accuracy
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const styles = window.getComputedStyle(inputElement);
      ctx.font = `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
      const metrics = ctx.measureText(textBeforeAt);

      // Account for input scroll offset
      const scrollOffset = inputElement.scrollLeft;

      setMenuPosition({
        top: rect.bottom,
        left: rect.left + metrics.width - scrollOffset,
      });
      setIsPositionReady(true);
    };

    if (!isPositionReady) {
      updatePosition();
    }

    // Update position when input changes (but only if menu is open)
    const handleInputChange = () => {
      if (isPositionReady) {
        updatePosition();
      }
    };

    const inputElement = document.querySelector('.mail-merge-subject');
    if (inputElement) {
      inputElement.addEventListener('input', handleInputChange);
      return () => inputElement.removeEventListener('input', handleInputChange);
    }
  }, [filterText, isSubjectMode, isPositionReady]);

  // For subject mode, position relative to input; for editor mode, position relative to button
  let top, left;
  if (isSubjectMode) {
    top = menuPosition.top;
    left = menuPosition.left;
  } else {
    const parent = editor?.editorContainer?.parentElement;
    const btn = parent?.querySelectorAll('.tox-tbtn');
    const dynamicTextBtn =
      Array.from(btn || []).find((b) => b.getAttribute('data-mce-name') === 'dynamictextbtn') ?? btn?.[4];
    const pos = dynamicTextBtn?.getBoundingClientRect() ?? {};
    top = pos.top;
    left = pos.left;
  }

  const dynamicTextPopup = useRef(null);
  const hideFilters = () => {
    setFilterText('');
    setActiveIndex(0);
    setOpenDynamicTextMenu(false);
    isInitialMount.current = true; // Reset for next time popup opens

    // Restore cursor position and focus when menu closes
    if (editor) {
      setTimeout(() => {
        try {
          // Try to restore bookmark position if available
          if (bookmark) {
            try {
              editor.selection.bookmarkManager.moveToBookmark(bookmark);
            } catch (e) {
              // Bookmark restore failed, continue with fallback
            }
          }

          // Check if cursor is inside a dynamicText element and move it if needed
          const range = editor.selection.getRng();
          let currentNode = range.startContainer;

          // Walk up the DOM tree to find if we're inside a dynamicText element
          let dynamicTextElement = null;
          while (currentNode && currentNode !== editor.getBody()) {
            if (currentNode.nodeType === 1) {
              if (currentNode.classList && currentNode.classList.contains('crm-mail-merge-substitution')) {
                dynamicTextElement = currentNode;
                break;
              }
            }
            currentNode = currentNode.parentNode;
          }

          // If we're inside a dynamicText element, move cursor after it
          if (dynamicTextElement) {
            const isInside =
              dynamicTextElement.contains(range.startContainer) ||
              (range.startContainer.nodeType === 1 && range.startContainer === dynamicTextElement);

            if (isInside) {
              range.setStartAfter(dynamicTextElement);
              range.collapse(true);
              editor.selection.setRng(range);
            }
          }

          // Restore focus to editor
          editor.focus();
        } catch (err) {
          // If there's an error, just restore focus
          try {
            editor.focus();
          } catch (e) {
            // Ignore focus errors
          }
        }
      }, 0);
    }
  };
  useOutsideElement(dynamicTextPopup, hideFilters);

  // Get text after @ sign in currentNode and update as user types
  useEffect(() => {
    if (!editor) return;

    const getTextAfterAt = (skipInitial = false) => {
      try {
        const currentRange = editor.selection.getRng();

        // First, try to find @ sign in current text node (most reliable when user is typing)
        const currentNode = currentRange.startContainer;
        const offset = currentRange.startOffset;

        if (currentNode && currentNode.nodeType === 3) {
          const text = currentNode.textContent;

          // Check if @ is at current position
          if (offset < text.length && text[offset] === '@') {
            const foundText = text.slice(offset + 1);
            if (!skipInitial) {
              setFilterText(foundText);
            }
            return;
          }

          // Check if @ is right before cursor (most common case when typing)
          if (offset > 0 && text[offset - 1] === '@') {
            const foundText = text.slice(offset);
            if (!skipInitial) {
              setFilterText(foundText);
            }
            return;
          }

          // Search backwards in current text node for the nearest @ sign
          // This handles cases where cursor has moved forward after typing
          const textBeforeCursor = text.slice(0, offset);
          const lastAtIndex = textBeforeCursor.lastIndexOf('@');
          if (lastAtIndex !== -1) {
            // Get text between @ and cursor
            const textBetween = text.slice(lastAtIndex + 1, offset);
            // If there's no whitespace or special characters between @ and cursor, use this @
            // This covers both: cursor right after @ (empty textBetween) and cursor after typing chars
            if (!textBetween.match(/[\s<>]/)) {
              const foundText = text.slice(lastAtIndex + 1);
              if (!skipInitial) {
                setFilterText(foundText);
              }
              return;
            }
          }
        }

        // Fallback: Use bookmark to find the correct @ sign (for initial mount only)
        // When user is typing, we should have already found it in the current node above
        const closestAt = findClosestAtSign(editor, currentRange, atSignBookmarkRef.current, skipInitial);

        if (closestAt) {
          const { textNode, atIndex } = closestAt;
          const text = textNode.textContent;
          const foundText = text.slice(atIndex + 1);

          if (!skipInitial) {
            setFilterText(foundText);
          }
          return;
        }

        // If no @ found, clear filter (only if not skipping initial)
        if (!skipInitial) {
          setFilterText('');
        }
      } catch (err) {
        console.error('Error getting text after @ sign:', err);
        if (!skipInitial) {
          setFilterText('');
        }
      }
    };

    // Skip setting filterText when popup first opens (don't filter existing text)
    if (isInitialMount.current) {
      getTextAfterAt(true); // Skip initial filter
      isInitialMount.current = false;
    }

    // Listen to input events to update as user types
    const handleInput = () => {
      getTextAfterAt(false); // Allow filtering when user types
    };

    // Listen to keydown events for immediate feedback
    const handleKeyDown = () => {
      // Use setTimeout to ensure the text is updated after the key is processed
      setTimeout(() => {
        getTextAfterAt(false); // Allow filtering when user types
      }, 0);
    };

    // Attach event listeners
    editor.on('input', handleInput);
    editor.on('keydown', handleKeyDown);

    // Cleanup
    return () => {
      editor.off('input', handleInput);
      editor.off('keydown', handleKeyDown);
    };
  }, [editor]);

  // Filter fields based on filterText
  const allFields = ['salutation', ...mailMergeFields];
  const filteredFields = filterText
    ? allFields.filter((field) => field.toLowerCase().startsWith(filterText.toLowerCase()))
    : allFields;

  // Reset active index when filterText changes
  useEffect(() => {
    setActiveIndex(0);
  }, [filterText]);

  // Scroll active item into view
  useEffect(() => {
    if (filteredFields.length > 0 && activeIndex >= 0) {
      const activeField = filteredFields[activeIndex];
      if (activeField) {
        const activeItem = document.querySelector(`[data-field="${activeField}"]`);
        if (activeItem) {
          activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }
  }, [activeIndex, filteredFields]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleNavigationKeyDown = (e) => {
      // Close menu if whitespace is entered
      if (e.key === ' ' || e.key === 'Space' || /\s/.test(e.key) || (filterText === '' && e.key === 'Backspace')) {
        hideFilters();
        return;
      }

      // Only handle arrow keys and Enter when menu is open
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        if (filteredFields.length === 0) return;

        e.preventDefault();
        e.stopPropagation();

        if (e.key === 'ArrowDown') {
          setActiveIndex((prev) => (prev + 1) % filteredFields.length);
        } else if (e.key === 'ArrowUp') {
          setActiveIndex((prev) => (prev - 1 + filteredFields.length) % filteredFields.length);
        } else if (e.key === 'Enter') {
          // Select the active item
          const activeField = filteredFields[activeIndex];
          if (activeField) {
            // Trigger click on the active item
            const activeItem = document.querySelector(`[data-field="${activeField}"]`);
            if (activeItem) {
              activeItem.click();
            }
          }
        }
        return false;
      }
    };

    // For subject mode, attach to input element; for editor mode, attach to editor
    if (isSubjectMode) {
      const inputElement = document.querySelector('.mail-merge-subject');
      if (inputElement) {
        inputElement.addEventListener('keydown', handleNavigationKeyDown, true);
        return () => {
          inputElement.removeEventListener('keydown', handleNavigationKeyDown, true);
        };
      }
    } else if (editor) {
      // Attach keyboard event listener to editor with higher priority
      editor.on('keydown', handleNavigationKeyDown, true);
      // Cleanup
      return () => {
        editor.off('keydown', handleNavigationKeyDown, true);
      };
    }
  }, [editor, filteredFields, activeIndex, isSubjectMode]);

  const style = {
    top: `${top}px`,
    left: `${left}px`,
    transform: isSubjectMode ? 'none' : 'translateY(-100%)',
    maxHeight: '335px',
    opacity: isSubjectMode && !isPositionReady ? 0 : 1,
    pointerEvents: isSubjectMode && !isPositionReady ? 'none' : 'auto',
  };

  return (
    <>
      <div
        className='general-close-background z-max'
        onClick={() => {
          hideFilters();
        }}
      />
      <div ref={dynamicTextPopup} className='z-max position-absolute box box-shadow w-auto overflow-auto' style={style}>
        {filteredFields.length > 0 && (
          <div className='share-container position-relative specific-y-padding-8'>
            <div className='share-container-items flex-column'>
              {filteredFields.map((field, index) => (
                <DynamicTextItem
                  key={field}
                  field={field}
                  atSignBookmarkRef={atSignBookmarkRef}
                  setIsOpen={setOpenDynamicTextMenu}
                  editor={editor}
                  bookmark={bookmark}
                  filterText={filterText}
                  isActive={index === activeIndex}
                  onHover={() => setActiveIndex(index)}
                  isSubjectMode={isSubjectMode}
                  onSelectForSubject={onSelectForSubject}
                />
              ))}
            </div>
          </div>
        )}
        {(!hasMailMergeData || selectedContacts === 0) && (
          <div className={`share-container-items flex-column ${filteredFields.length > 0 ? 'border-top' : ''}`}>
            {selectedContacts === 0 ? (
              <button
                className='general-button specific-x-padding-24 specific-y-padding-8 d-flex flex-column align-items-start w-100 cursor-default'
                data-tooltip-content={'Please add recipients first'}
                data-tooltip-id={'general-tooltip'}
                data-tooltip-place={'top'}
                data-tooltip-class-name={'general-tooltip pricing-feature-tooltip report-popup-tooltip'}
              >
                <span className='paragraph-p1 text-light-blue py-1 d-flex align-items-center'>
                  <span className='specific-right-padding-4'>+</span> Create an attribute for this email
                </span>
              </button>
            ) : (
              <button
                onClick={() => {
                  autoSaveEmailOnMailMerge({
                    action: 'UseMailMerge',
                    setIsLoading: setIsSavingEmailOnMailMerge,
                    callback: () => {
                      setEmailState('mailMergeModalIsOpen', true);
                      setIsSavingEmailOnMailMerge(false);
                    },
                  });
                }}
                className={`general-button specific-x-padding-24 d-flex flex-column align-items-start w-100 hovered-button`}
              >
                <div
                  className={`${!isSavingEmailOnMailMerge ? 'checkbox-item' : ''} specific-top-margin-${
                    isSavingEmailOnMailMerge ? '10' : '14'
                  } specific-bottom-margin-${isSavingEmailOnMailMerge ? '12' : '8'} ${
                    selectedContacts === 0 ? 'opacity-5' : ''
                  }`}
                >
                  {isSavingEmailOnMailMerge ? (
                    <i className='fas fa-spinner fa-spin d-inline-block-centered specific-right-margin-3' />
                  ) : (
                    <input id={`use-mail-merge`} type={'checkbox'} checked={hasMailMergeData} />
                  )}

                  <label htmlFor={`use-mail-merge`} className='pl-3 mb-0'>
                    <span>Use mail merge to add attributes</span>
                  </label>
                </div>
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const DynamicTextItem = ({
  setIsOpen,
  editor,
  field,
  atSignBookmarkRef,
  bookmark,
  filterText,
  isActive,
  onHover,
  isSubjectMode = false,
  onSelectForSubject = null,
}) => {
  const removeAtSign = () => {
    if (!editor) return; // Skip if no editor (subject mode)
    try {
      const currentRange = editor.selection.getRng();

      // Use bookmark to find the correct @ sign (bookmark was stored right before @ was typed)
      // If no bookmark, use current cursor position
      const closestAt = findClosestAtSign(editor, currentRange, atSignBookmarkRef.current);

      if (closestAt) {
        const { textNode, atIndex } = closestAt;
        const text = textNode.textContent;

        // Check if filterText follows the @ sign
        const textAfterAt = text.slice(atIndex + 1);
        if (filterText && textAfterAt.startsWith(filterText)) {
          // Remove @ and filterText
          textNode.deleteData(atIndex, 1 + filterText.length);
          const newRange = document.createRange();
          newRange.setStart(textNode, atIndex);
          newRange.collapse(true);
          editor.selection.setRng(newRange);
          atSignBookmarkRef.current = null;
          return;
        } else {
          // Just remove @
          textNode.deleteData(atIndex, 1);
          const newRange = document.createRange();
          newRange.setStart(textNode, atIndex);
          newRange.collapse(true);
          editor.selection.setRng(newRange);
          atSignBookmarkRef.current = null;
          return;
        }
      }

      // Fallback: check current text node directly
      const currentNode = currentRange.startContainer;
      const offset = currentRange.startOffset;

      if (currentNode && currentNode.nodeType === 3) {
        const text = currentNode.textContent;

        // Check if "@" is at the current position
        if (offset < text.length && text[offset] === '@') {
          const textAfterAt = text.slice(offset + 1);
          if (filterText && textAfterAt.startsWith(filterText)) {
            currentNode.deleteData(offset, 1 + filterText.length);
            const newRange = document.createRange();
            newRange.setStart(currentNode, offset);
            newRange.collapse(true);
            editor.selection.setRng(newRange);
            atSignBookmarkRef.current = null;
            return;
          } else {
            currentNode.deleteData(offset, 1);
            const newRange = document.createRange();
            newRange.setStart(currentNode, offset);
            newRange.collapse(true);
            editor.selection.setRng(newRange);
            atSignBookmarkRef.current = null;
            return;
          }
        }

        // Check if "@" is right before the cursor
        if (offset > 0 && text[offset - 1] === '@') {
          const textAfterAt = text.slice(offset);
          if (filterText && textAfterAt.startsWith(filterText)) {
            currentNode.deleteData(offset - 1, 1 + filterText.length);
            const newRange = document.createRange();
            newRange.setStart(currentNode, offset - 1);
            newRange.collapse(true);
            editor.selection.setRng(newRange);
            atSignBookmarkRef.current = null;
            return;
          } else {
            currentNode.deleteData(offset - 1, 1);
            const newRange = document.createRange();
            newRange.setStart(currentNode, offset - 1);
            newRange.collapse(true);
            editor.selection.setRng(newRange);
            atSignBookmarkRef.current = null;
            return;
          }
        }
      }

      atSignBookmarkRef.current = null;
    } catch (err) {
      console.error('Error removing @ sign:', err);
      atSignBookmarkRef.current = null;
    }
  };

  const getAction = () => {
    // Handle subject mode - simple plain text insertion
    if (isSubjectMode && onSelectForSubject) {
      onSelectForSubject(field);
      return;
    }

    // Handle editor mode (existing logic)
    setIsOpen((val) => !val);
    editor.focus();

    // Get the bookmark for the insertion position (where "@" was typed)
    let currentBookmark = bookmark;
    if (!currentBookmark && atSignBookmarkRef.current) {
      currentBookmark = atSignBookmarkRef.current;
    }
    if (!currentBookmark) {
      // Fallback: get current bookmark
      currentBookmark = editor.selection.bookmarkManager.getBookmark(2, true);
    }

    // Restore cursor position to where "@" was typed
    if (currentBookmark) {
      try {
        editor.selection.bookmarkManager.moveToBookmark(currentBookmark);
      } catch (err) {
        // If bookmark restore fails, try to use atSignBookmarkRef
        if (atSignBookmarkRef.current) {
          try {
            editor.selection.bookmarkManager.moveToBookmark(atSignBookmarkRef.current);
          } catch (e) {
            // If that also fails, cursor should still be in a reasonable position
          }
        }
      }
    }

    // Remove "@" sign at the current cursor position FIRST
    // This ensures we're working with the correct cursor position
    if (removeAtSign && typeof removeAtSign === 'function') {
      removeAtSign();
    }

    // Now check if cursor is inside or adjacent to an existing dynamicText element
    // and move after it if needed (AFTER removing "@" so we don't move before "@")
    const range = editor.selection.getRng();
    let currentNode = range.startContainer;

    // Walk up the DOM tree to find if we're inside a dynamicText element
    let dynamicTextElement = null;
    while (currentNode && currentNode !== editor.getBody()) {
      if (currentNode.nodeType === 1) {
        // Element node
        if (currentNode.classList && currentNode.classList.contains('crm-mail-merge-substitution')) {
          dynamicTextElement = currentNode;
          break;
        }
      }
      currentNode = currentNode.parentNode;
    }

    // If we're inside a dynamicText element, move cursor after it
    // Only move if we're actually inside it (not just adjacent)
    if (dynamicTextElement) {
      const newRange = editor.selection.getRng();

      // Check if cursor is actually inside the dynamicText element
      // (not just in a sibling node after it)
      const isInside =
        dynamicTextElement.contains(newRange.startContainer) ||
        (newRange.startContainer.nodeType === 1 && newRange.startContainer === dynamicTextElement);

      if (isInside) {
        // We're inside the dynamicText, move after it
        newRange.setStartAfter(dynamicTextElement);
        newRange.collapse(true);
        editor.selection.setRng(newRange);
      }
      // If we're not inside it, don't move - we're already in the right position
    }

    // Ensure no selection is active (collapse to a point) - this prevents replacing existing content
    const currentRange = editor.selection.getRng();
    if (!currentRange.collapsed) {
      currentRange.collapse(true);
      editor.selection.setRng(currentRange);
    }

    // Double-check we're not inside a dynamicText element after all adjustments
    const finalCheckRange = editor.selection.getRng();
    let finalCheckNode = finalCheckRange.startContainer;
    while (finalCheckNode && finalCheckNode !== editor.getBody()) {
      if (
        finalCheckNode.nodeType === 1 &&
        finalCheckNode.classList &&
        finalCheckNode.classList.contains('crm-mail-merge-substitution')
      ) {
        // Move cursor after this element
        finalCheckRange.setStartAfter(finalCheckNode);
        finalCheckRange.collapse(true);
        editor.selection.setRng(finalCheckRange);
        break;
      }
      finalCheckNode = finalCheckNode.parentNode;
    }

    // Ensure no dynamicText element is selected (this would cause replacement instead of insertion)
    const selectedNode = editor.selection.getNode();
    if (selectedNode && selectedNode.classList && selectedNode.classList.contains('crm-mail-merge-substitution')) {
      // If a dynamicText is selected, move cursor after it
      const safeRange = editor.selection.getRng();
      safeRange.setStartAfter(selectedNode);
      safeRange.collapse(true);
      editor.selection.setRng(safeRange);
    }

    // Generate a unique identifier for this insertion
    const uniqueId = `merge-field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // Get the HTML and add the unique data attribute
    let content = dynamicTextImage(field);
    content = content.replace(
      '<span contenteditable="false"',
      `<span contenteditable="false" data-insert-id="${uniqueId}"`
    );

    // Insert the content at current cursor position
    editor.insertContent(content);

    // Find the element with this unique identifier
    const insertedElement = editor.getBody().querySelector(`[data-insert-id="${uniqueId}"]`);

    if (insertedElement) {
      // Position caret after the inserted element
      const finalRange = editor.selection.getRng();
      finalRange.setStartAfter(insertedElement);
      finalRange.collapse(true);
      editor.selection.setRng(finalRange);

      // Insert whitespace after the attribute
      editor.insertContent(' ');

      // Position cursor after the whitespace
      // TinyMCE's insertContent positions cursor after inserted content, but we need to ensure
      // it's positioned after the space text node
      const spaceRange = editor.selection.getRng();
      const nextSibling = insertedElement.nextSibling;

      if (nextSibling && nextSibling.nodeType === 3) {
        // The space was inserted as a text node, position cursor at the end of it (after the space)
        spaceRange.setStart(nextSibling, nextSibling.textContent.length);
        spaceRange.collapse(true);
        editor.selection.setRng(spaceRange);
      } else {
        // If space wasn't inserted as expected, ensure cursor is positioned correctly
        // The insertContent should have already positioned it correctly, but verify
        const currentRange = editor.selection.getRng();
        if (currentRange.startContainer.nodeType === 3) {
          // We're in a text node, ensure we're at the end
          const textNode = currentRange.startContainer;
          currentRange.setStart(textNode, textNode.textContent.length);
          currentRange.collapse(true);
          editor.selection.setRng(currentRange);
        }
      }
    } else {
      // Fallback: position at end of body
      editor.selection.select(editor.getBody(), true);
      editor.selection.collapse(false);
    }
  };

  return (
    <button
      data-field={field}
      className={`general-button specific-x-padding-24 specific-top-padding-8 specific-bottom-padding-8 d-flex flex-column align-items-start w-100 hovered-button ${
        isActive ? 'bg-blue-hovered' : ''
      }`}
      onClick={getAction}
      onMouseEnter={onHover}
    >
      <span className='font-weight-bold paragraph-p1'>{field}</span>
      <span className='text-main-blue-60 paragraph-p1'>
        Attribute added {field === 'salutation' ? 'by Policymogul' : 'via mail merge'}
      </span>
    </button>
  );
};

const openDynamicTextMenuFunction = (editor, dynamicTextBtnApiRef) => {
  // Try to find the button using multiple methods
  const editorContainer = editor.editorContainer;
  if (!editorContainer) return;

  let button = null;

  // Method 1: Find by icon class (most reliable)
  const toolbar = editorContainer.querySelector('.tox-toolbar');
  if (toolbar) {
    const buttons = toolbar.querySelectorAll('.tox-tbtn, .tox-split-button, button[role="button"]');
    for (let btn of buttons) {
      const icon = btn.querySelector('.salutation-toolbar-button');
      if (icon) {
        button = btn;
        break;
      }
    }

    // Method 2: Find by @ symbol content
    if (!button) {
      for (let btn of buttons) {
        const btnText = btn.textContent || btn.innerText || '';
        const btnHtml = btn.innerHTML || '';
        if (btnText.includes('@') || btnHtml.includes('@') || btnHtml.includes('salutation-toolbar-button')) {
          button = btn;
          break;
        }
      }
    }
  }

  // Method 3: Search entire editor container
  if (!button) {
    const allButtons = editorContainer.querySelectorAll('button, .tox-tbtn, .tox-split-button');
    for (let btn of allButtons) {
      if (btn.querySelector('.salutation-toolbar-button')) {
        button = btn;
        break;
      }
    }
  }

  // Method 4: Try using TinyMCE UI registry API
  if (!button && dynamicTextBtnApiRef) {
    try {
      const el = dynamicTextBtnApiRef.getEl();
      if (el) button = el;
    } catch (err) {
      // API method not available
    }
  }

  // Click the button if found
  if (button) {
    // Focus the editor first
    editor.focus();

    // For split buttons, try clicking the chevron/dropdown part
    if (button.classList.contains('tox-split-button')) {
      const chevron = button.querySelector('.tox-split-button__chevron, .tox-split-button__select-label');
      if (chevron) {
        chevron.click();
        return;
      }
    }

    // Try multiple click methods
    try {
      button.click();
    } catch (err) {
      // If click fails, try dispatching event
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
        button: 0,
      });
      button.dispatchEvent(clickEvent);
    }
  }
};

export { openDynamicTextMenuFunction };
export default DynamicTextMenu;
